import React, { memo, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import isEqual from 'react-fast-compare';
import { focusManager } from '@accedo/vdkweb-navigation';

import styles from './swimlane.module.scss';
import useIsMounted from '../../hooks/useIsMounted';
import useLatest from '../../hooks/useLatest';
import FocusDiv from '../focus-div/FocusDiv';
import { useDispatch, useSelector } from 'react-redux';
import { saveLastFocusedTileId } from '../../redux/action/xdk.action';
import { getLastFocusedTileId } from '../../redux/selector/xdk.store';

const SwimlaneItemWrapper = <ItemDataType,>({
  index,
  onItemFocus,
  className,
  component: Component,
  nav,
  swimlaneId,
  pageId,
  ...props
}: SwimlaneItemWrapperProps<ItemDataType>) => {
  const isMounted = useIsMounted();
  const [clicked, setClicked] = useState(0);
  const [isFocused, setIsFocused] = useState(
    focusManager.getCurrentFocus() === nav.id,
  );
  const lastClickedValueRef = useLatest(clicked);
  const lastFocusedTile = useSelector(getLastFocusedTileId);
  const dispatch = useDispatch();

  const onFocus = useCallback(() => {
    setIsFocused(true);
    onItemFocus(index);
  }, []);

  const onBlur = useCallback(() => {
    if (isMounted()) {
      setIsFocused(false);
    }
  }, []);

  const onClick = useCallback(() => {
    if (isMounted()) {
      dispatch(
        saveLastFocusedTileId({
          ...lastFocusedTile,
          [pageId]: nav.id,
        }),
      );
      setClicked(lastClickedValueRef.current + 1);
    }
  }, []);

  // Enter if an item initial render is focused
  useEffect(() => {
    if (isFocused) {
      onItemFocus?.(index);
    }
  }, []);

  return (
    <FocusDiv
      nav={nav}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
      className={classNames(styles.itemWrapper, className)}
    >
      <Component
        isFocused={isFocused}
        navId={nav.id}
        clicked={clicked}
        swimlaneId={swimlaneId}
        index={index}
        tileClick={onClick}
        {...props}
      />
    </FocusDiv>
  );
};

export default memo(SwimlaneItemWrapper, isEqual);
